import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DataTable, useDataTable, type DataTableMode } from '@odo/shared-ui';
import { useSearch } from '../../contexts/SearchContext';
import { useViewportPageSize } from '../../hooks/useViewportPageSize';
import {
    PracticeListItemType,
    usePracticesList
} from '../../providers/practices/PracticesListProvider';
import Loading from '../Loading';
import { buildPracticeColumns, type PracticesSortBy } from './practiceColumns';

// Chunk revealed per "Load more" step in infinite mode - matches shared-ui
// DataTable's own infinite-mode default chunk size.
const INFINITE_CHUNK_SIZE = 50;

// Same convention as PatientTable.tsx/InvoicesSection.tsx (localStorage,
// page-scoped key); default infinite since practices already run ~360.
const VIEW_MODE_KEY = 'practicesTableViewMode';

function readStoredMode(): DataTableMode {
    return localStorage.getItem(VIEW_MODE_KEY) === 'paged' ? 'paged' : 'infinite';
}

function compareByField(
    a: PracticeListItemType,
    b: PracticeListItemType,
    field: PracticesSortBy,
    order: 'asc' | 'desc'
): number {
    const valueA = a[field] || '';
    const valueB = b[field] || '';
    return (
        valueA.localeCompare(valueB, undefined, { sensitivity: 'base' }) *
        (order === 'asc' ? 1 : -1)
    );
}

const PracticesTable: React.FC = () => {
    const { practices, isLoading, error } = usePracticesList();
    const { searchTerm } = useSearch();
    const [initialMode] = useState<DataTableMode>(readStoredMode);
    const [revealedCount, setRevealedCount] = useState(INFINITE_CHUNK_SIZE);

    const table = useDataTable<PracticesSortBy>({
        defaultSort: { by: 'name', order: 'asc' },
        mode: initialMode
    });
    const { setLimit, setPage, setMode, toggleSort } = table;

    const { pageSize, tableCardRef, tableHeaderRef, tableBodyRef, paginationRef, remeasureOnce } =
        useViewportPageSize();

    // The `home` route's SidebarLayout has no `noScroll`, so `h-full` has no
    // bounded ancestor: measure our own height, as PatientTable.tsx does.
    const containerRef = useRef<HTMLDivElement>(null);
    const [availableHeight, setAvailableHeight] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        function handleResize() {
            if (containerRef.current) {
                const top = containerRef.current.getBoundingClientRect().top;
                setAvailableHeight(window.innerHeight - top);
            }
        }

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isLoading]);

    // Also re-syncs on mode change: setLimit no-ops while infinite, so
    // switching to paged would otherwise stick at useDataTable's default limit.
    useEffect(() => {
        setLimit(pageSize);
    }, [pageSize, setLimit, table.mode]);

    // Reset page and revealed count on search change; sort changes already
    // reset both via handleSortToggle.
    useEffect(() => {
        setPage(1);
        setRevealedCount(INFINITE_CHUNK_SIZE);
    }, [searchTerm, setPage]);

    const handleSortToggle = useCallback(
        (by: PracticesSortBy) => {
            toggleSort(by);
            setRevealedCount(INFINITE_CHUNK_SIZE);
        },
        [toggleSort]
    );

    const handleModeChange = useCallback(
        (mode: DataTableMode) => {
            setMode(mode);
            localStorage.setItem(VIEW_MODE_KEY, mode);
            setRevealedCount(INFINITE_CHUNK_SIZE);
        },
        [setMode]
    );

    const filteredPractices = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return practices;
        return practices.filter((practice) => {
            const practiceName = (practice.name || '').toLowerCase();
            const doctorName = `dr. ${practice.doctorName || ''}`.toLowerCase();
            return practiceName.includes(term) || doctorName.includes(term);
        });
    }, [practices, searchTerm]);

    const sortedPractices = useMemo(
        () => [...filteredPractices].sort((a, b) => compareByField(a, b, table.sort, table.order)),
        [filteredPractices, table.sort, table.order]
    );

    const totalPages = Math.max(1, Math.ceil(sortedPractices.length / table.limit));

    // Clamp if pageSize or filtering shrinks the list out from under the
    // current page.
    useEffect(() => {
        if (table.page > totalPages) setPage(totalPages);
    }, [totalPages, table.page, setPage]);

    const paginatedPractices = useMemo(() => {
        const start = (table.page - 1) * table.limit;
        return sortedPractices.slice(start, start + table.limit);
    }, [sortedPractices, table.page, table.limit]);

    // Infinite mode windows the sorted/filtered array instead of mounting it
    // all - same shape as InvoicesSection.tsx and PatientTable.tsx.
    const revealedPractices = useMemo(
        () => sortedPractices.slice(0, revealedCount),
        [sortedPractices, revealedCount]
    );
    const hasMorePractices = revealedCount < sortedPractices.length;
    const handleLoadMorePractices = useCallback(() => {
        setRevealedCount((prev) => Math.min(prev + INFINITE_CHUNK_SIZE, sortedPractices.length));
    }, [sortedPractices.length]);

    const displayedPractices = table.mode === 'infinite' ? revealedPractices : paginatedPractices;

    // Re-measure once real rows render: the empty/loading state is a different
    // height and would skew the first measurement.
    useLayoutEffect(() => {
        if (displayedPractices.length === 0) return;
        remeasureOnce();
    }, [displayedPractices.length, remeasureOnce]);

    const columns = useMemo(() => buildPracticeColumns(), []);

    if (isLoading) return <Loading />;
    if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

    return (
        <div
            className="px-8 py-4 w-full flex flex-col overflow-hidden"
            ref={containerRef}
            style={{ height: availableHeight }}
        >
            <DataTable
                label="Practices"
                columns={columns}
                rows={displayedPractices}
                getRowKey={(practice) => practice.id}
                rowClassName={() =>
                    'hover:bg-gray-50 transition-colors duration-150 text-sm text-gray-700'
                }
                rowHeightClassName="h-16"
                sort={{ by: table.sort, order: table.order, onSort: handleSortToggle }}
                mode={table.mode}
                onModeChange={handleModeChange}
                onLoadMore={handleLoadMorePractices}
                hasMore={hasMorePractices}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: sortedPractices.length,
                    onPage: setPage
                }}
                cardHeader={
                    searchTerm ? (
                        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                            <p className="text-sm text-gray-600">
                                {sortedPractices.length === 0
                                    ? `No practices found matching "${searchTerm}"`
                                    : `Found ${sortedPractices.length} practice${sortedPractices.length === 1 ? '' : 's'} matching "${searchTerm}"`}
                            </p>
                        </div>
                    ) : undefined
                }
                emptyState="No practices found."
                refs={{
                    card: tableCardRef,
                    head: tableHeaderRef,
                    body: tableBodyRef,
                    pagination: paginationRef
                }}
                className="flex-1 min-h-0"
            />
        </div>
    );
};

export default PracticesTable;
